import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

interface ShortlistModalProps {
  open: boolean;
  onClose: () => void;
  onProceed: (selectedItems: string[]) => void;
  attachments: any[];
  isSubmitting?: boolean;
}

const ShortlistModal: React.FC<ShortlistModalProps> = ({
  open,
  onClose,
  onProceed,
  attachments = [],
  isSubmitting = false,
}) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [expanded, setExpanded] = useState(false);

  // Default select all when modal opens
  React.useEffect(() => {
    if (open) {
      const attachmentIds = attachments.map((a) => String(a.id || a.fileId));
      setSelectedItems([
        'Resume',
        'Candidate Overview Summary',
        ...attachmentIds,
      ]);
    }
  }, [open, attachments]);

  const attachmentCount = attachments.length;
  // File IDs of individual attachments
  const attachmentIds = attachments.map((a) => String(a.id || a.fileId));
  const allAttachmentsSelected =
    attachmentCount > 0 &&
    attachmentIds.every((id) => selectedItems.includes(id));

  const toggleItem = (item: string) => {
    setSelectedItems((prev) => {
      if (prev.includes(item)) {
        return prev.filter((i) => i !== item);
      } else {
        return [...prev, item];
      }
    });
  };

  const toggleAllAttachments = () => {
    setSelectedItems((prev) => {
      const otherItems = prev.filter((i) => !attachmentIds.includes(i));
      if (allAttachmentsSelected) {
        // Unselect all
        return otherItems;
      } else {
        // Select all
        return [...otherItems, ...attachmentIds];
      }
    });
  };

  const handleProceed = () => {
    onProceed(selectedItems);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="shortlist-modal"
      fullWidth
      maxWidth="xs">
      <Box className="shortlist-header">
        <Box className="header-content">
          <Typography className="title">Select Items to Include</Typography>
          <Typography className="subtitle">
            These are the final documents being submitted.
          </Typography>
        </Box>
        <IconButton onClick={onClose} className="close-icon-btn" size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box className="shortlist-content">
        <Box className="item-row" onClick={() => toggleItem('Resume')}>
          <Box className="item-left">
            <Box
              className={`custom-checkbox ${
                selectedItems.includes('Resume') ? 'checked' : ''
              }`}>
              {selectedItems.includes('Resume') && (
                <CheckIcon sx={{ fontSize: 13 }} />
              )}
            </Box>
            <Typography className="item-label">Resume</Typography>
          </Box>
        </Box>

        <Box
          className="item-row"
          onClick={() => toggleItem('Candidate Overview Summary')}>
          <Box className="item-left">
            <Box
              className={`custom-checkbox ${
                selectedItems.includes('Candidate Overview Summary')
                  ? 'checked'
                  : ''
              }`}>
              {selectedItems.includes('Candidate Overview Summary') && (
                <CheckIcon sx={{ fontSize: 13 }} />
              )}
            </Box>
            <Typography className="item-label">
              Candidate Overview Summary
            </Typography>
          </Box>
        </Box>

        <Box className="item-row" onClick={() => toggleAllAttachments()}>
          <Box className="item-left">
            <Box
              className={`custom-checkbox ${
                allAttachmentsSelected ? 'checked' : ''
              }`}>
              {allAttachmentsSelected && <CheckIcon sx={{ fontSize: 13 }} />}
            </Box>
            <Typography className="item-label">
              All Attachments ({attachmentCount} files)
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            className="dropdown-icon-btn">
            {expanded ? (
              <KeyboardArrowUpIcon className="dropdown-icon" />
            ) : (
              <KeyboardArrowDownIcon className="dropdown-icon" />
            )}
          </IconButton>
        </Box>

        {expanded && attachmentCount > 0 && (
          <Box className="expanded-attachments">
            {attachments.map((attachment) => {
              const id = String(attachment.id || attachment.fileId);
              const name =
                attachment.fileName ||
                attachment.name ||
                attachment?.file?.fileName ||
                'Unnamed File';
              return (
                <Box
                  key={id}
                  className="sub-item-row"
                  onClick={() => toggleItem(id)}>
                  <Box className="item-left">
                    <Box
                      className={`custom-checkbox sub-checkbox ${
                        selectedItems.includes(id) ? 'checked' : ''
                      }`}>
                      {selectedItems.includes(id) && (
                        <CheckIcon sx={{ fontSize: 13 }} />
                      )}
                    </Box>
                    <Typography className="sub-item-label">{name}</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Box className="shortlist-footer">
        <Button onClick={onClose} className="cancel-btn-shortlist">
          Cancel
        </Button>
        <Button
          onClick={handleProceed}
          className="proceed-btn-shortlist"
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={16} sx={{ color: '#ffffff' }} />
            ) : null
          }>
          {isSubmitting ? 'Proceeding...' : 'Proceed'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default ShortlistModal;
